import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * ErrorBoundary — catches unhandled React render errors so the app
 * never goes fully white. Shows a styled recovery screen instead.
 * Use at the route level in App.tsx (wraps each lazy page).
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Sentry will catch this automatically via its BrowserTracing integration.
    // We log to console as a fallback for dev.
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack)
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: '1.5rem',
            background: 'var(--bg-base)',
            fontFamily: 'var(--font-body)',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <div style={{
            width: '4rem', height: '4rem', borderRadius: '1.25rem',
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'page-enter 300ms cubic-bezier(0.16,1,0.3,1)',
          }}>
            <AlertTriangle size={24} style={{ color: '#EF4444' }} />
          </div>

          {/* Message */}
          <div style={{ maxWidth: '28rem' }}>
            <h1 style={{
              fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)',
              marginBottom: '0.5rem', letterSpacing: '-0.02em',
            }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              This page encountered an unexpected error. Your session and data are safe.
            </p>
            {this.state.error && (
              <code style={{
                display: 'block', marginTop: '1rem', padding: '0.75rem 1rem',
                background: 'var(--bg-elevated)', borderRadius: '0.5rem',
                fontSize: '0.75rem', color: '#F87171',
                border: '1px solid rgba(239,68,68,0.15)',
                textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {this.state.error.message}
              </code>
            )}
          </div>

          {/* Recovery actions */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={this.handleReload}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                background: 'var(--brand-primary)', color: '#fff',
                border: 'none', borderRadius: '0.5rem', cursor: 'pointer',
                fontSize: '0.875rem', fontWeight: 600,
                transition: 'opacity 200ms',
              }}
            >
              <RefreshCw size={14} /> Try Again
            </button>
            <button
              onClick={() => window.location.replace('/dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                border: '1px solid var(--border-default)', borderRadius: '0.5rem',
                cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
                transition: 'opacity 200ms',
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
